'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDownIcon } from 'lucide-react'

import { cn } from '../lib/utils'
import { Button } from './button'
import { Input } from './input'
import { Textarea } from './textarea'

function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        // Border + surface match the `Input` family exactly — hairline
        // via the `border-border` token (the single product-wide border),
        // transparent surface, no dark fill — so a group sits flush with
        // plain inputs in a form.
        'group/input-group relative flex h-8 w-full min-w-0 items-center rounded-lg border border-border bg-transparent transition-colors outline-none in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0 has-disabled:opacity-50 has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-3 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-3 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        className
      )}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start':
          'order-first pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]',
        'inline-end':
          'order-last pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end':
          'order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
)

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        // Don't steal focus from interactive addon content — refocusing the
        // input closes a native <select>'s popup the instant it opens.
        if (
          (e.target as HTMLElement).closest(
            'button, select, a, input, textarea, label'
          )
        ) {
          return
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva(
  'flex items-center gap-2 text-sm shadow-none',
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc(var(--radius)-3px)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
        sm: '',
        'icon-xs':
          'size-6 rounded-[calc(var(--radius)-3px)] p-0 has-[>svg]:p-0',
        'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  }
)

function InputGroupButton({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset'
  }) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

/**
 * The group's text control. Borderless and transparent: the GROUP draws the
 * border and the focus ring.
 *
 * `h-full min-h-0`, not `Input`'s own `h-10`: the input must sit INSIDE the
 * group's border, never over it. `Input` is 40px tall, so inside a 40px group
 * (1px border each side) it overlapped the top and bottom border by a pixel, and
 * inside the default 32px group by four. Invisible while the input is
 * transparent, but browser autofill paints the input opaque (the `autofill:`
 * inset shadow in `Input` that masks Chrome's yellow) and that paint then
 * covered the group's top and bottom border along the input's whole width,
 * leaving the border drawn only round the addon. Filling the content box keeps
 * every pixel of that paint inside the border; `min-h-0` lets the flex item
 * shrink below its padding + line box in the 32px group, where the browser
 * still centres the text.
 *
 * The inner radius (group radius minus its 1px border) for the same reason: a
 * square-cornered fill would nick the group's rounded corners.
 */
function InputGroupInput({
  className,
  ...props
}: React.ComponentProps<'input'>) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'h-full min-h-0 flex-1 rounded-[calc(var(--radius-lg)-1px)] border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent',
        className
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent',
        className
      )}
      {...props}
    />
  )
}

/**
 * A native select as the group's control — for "picker + adjacent action"
 * rows (e.g. an account picker with a + connect button). Borderless and
 * transparent like `InputGroupInput`; the group draws the border and the
 * focus ring (via `data-slot="input-group-control"`).
 *
 * `grow` (default) stretches the select to fill the group — right when the
 * select IS the main control. Pass `grow={false}` for a trailing unit
 * select beside an input (e.g. a number + days/hours picker): the select
 * hugs its content at the group's end instead of stranding the label far
 * from its chevron.
 */
function InputGroupSelect({
  className,
  children,
  grow = true,
  ...props
}: React.ComponentProps<'select'> & { grow?: boolean }) {
  return (
    <span
      className={cn(
        'relative flex min-w-0 items-center',
        grow ? 'flex-1' : 'flex-none'
      )}
    >
      <select
        data-slot="input-group-control"
        className={cn(
          'appearance-none rounded-none border-0 bg-transparent px-2.5 py-1 pr-8 text-sm outline-none disabled:bg-transparent',
          grow ? 'w-full' : 'w-auto',
          className
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon
        aria-hidden
        className="pointer-events-none absolute right-2.5 size-4 text-muted-foreground"
      />
    </span>
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupSelect,
  InputGroupTextarea,
}
